// Phase 43 — <ConflictChooser /> (Plan 43-04 Task 2, CANVAS-04, D-07).
// Inline chooser for multi-source fields. Two variants:
//   - radio:    single winner (e.g. manufacturer — URL vs VIN conflict)
//   - checkbox: merge-capable (e.g. equipment tags)
// Option label format locked by UI-SPEC: "{source} · {sourceContext} ({confidence}%)"
// Typography lock (UI-SPEC rev 2): 13px at weight 400 (text-[0.8125rem]
// font-normal). The 12px size and weight-500 class names are disallowed.

import { RadioGroup, RadioGroupItem } from '@marine/ui/radio-group';
import { Checkbox } from '@marine/ui/checkbox';
import { Label } from '@marine/ui/label';
import { sourceLabel } from './ProvenanceChip';
import { humanizeToken } from '@/modules/core/utils/vocabulary';
import type { ProvenanceEntry } from '@/modules/inventory/types/boat.types';

interface ConflictChooserProps {
  fieldName: string;
  variant: 'radio' | 'checkbox';
  entries: ProvenanceEntry[];
  selectedIndex: number | null; // radio
  selectedIndexes?: number[]; // checkbox
  onSelectOne?: (entry: ProvenanceEntry) => void; // radio
  onToggle?: (entry: ProvenanceEntry, checked: boolean) => void; // checkbox
}

/**
 * The value as the dealer reads it. #1337 — a set (boat_types) is its labels joined,
 * never JSON: the tokens are humanized (`ski_boat` → "Ski Boat"), which matches the
 * seeded catalogue labels and the trigger's own off-list fallback.
 */
function formatContext(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? humanizeToken(v) : String(v))).join(', ');
  }
  return JSON.stringify(value);
}

function formatOptionLabel(entry: ProvenanceEntry): string {
  const pct = Math.round(entry.confidence * 100);
  const context = formatContext(entry.value);
  // Verbatim format per UI-SPEC §"Conflict UI copy"; the source shows its display name (#1307).
  return `${sourceLabel(entry.source)} · ${context} (${pct}%)`;
}

export function ConflictChooser({
  fieldName,
  variant,
  entries,
  selectedIndex,
  selectedIndexes = [],
  onSelectOne,
  onToggle,
}: ConflictChooserProps) {
  const headingId = `conflict-heading-${fieldName}`;

  if (variant === 'radio') {
    return (
      <div className="space-y-1.5">
        {/* 13px font-normal per UI-SPEC §"Component Inventory" */}
        <p
          id={headingId}
          className="text-[0.8125rem] font-normal text-foreground"
        >
          Choose which source to use:
        </p>
        <RadioGroup
          value={selectedIndex !== null ? String(selectedIndex) : undefined}
          onValueChange={(v) => {
            const i = Number(v);
            const entry = entries[i];
            if (entry && onSelectOne) onSelectOne(entry);
          }}
          aria-labelledby={headingId}
          className="space-y-1"
        >
          {entries.map((entry, i) => {
            const id = `option-${fieldName}-${i}`;
            return (
              <div key={id} className="flex items-center gap-2">
                <RadioGroupItem value={String(i)} id={id} />
                <Label
                  htmlFor={id}
                  className="text-[0.8125rem] font-normal"
                >
                  {formatOptionLabel(entry)}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      </div>
    );
  }

  // Checkbox variant (merge-capable)
  return (
    <div className="space-y-1.5">
      <p
        id={headingId}
        className="text-[0.8125rem] font-normal text-foreground"
      >
        Select all that apply:
      </p>
      <div aria-labelledby={headingId} className="space-y-1">
        {entries.map((entry, i) => {
          const id = `cb-${fieldName}-${i}`;
          const checked = selectedIndexes.includes(i);
          return (
            <div key={id} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={(next) => onToggle?.(entry, Boolean(next))}
              />
              <Label htmlFor={id} className="text-[0.8125rem] font-normal">
                {formatOptionLabel(entry)}
              </Label>
            </div>
          );
        })}
      </div>
      <p className="text-[0.8125rem] font-normal text-muted-foreground">
        Your selection will be saved when you edit another field.
      </p>
    </div>
  );
}
